import React, { useEffect, useMemo, useState } from "react"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import useSearchParams, { setSearchParams } from "../hooks/useSearchParams"
import { showErrorToast } from "aldoo-ra/Toast"
import useT from "../hooks/useT"
import Img from "../ui/Img"
import useOrders, { isDigitalItem } from "./useOrders"
import { DeliverableLink, deliverableUrl } from "../storeApi"
import { buildContentUrl } from "../utils/contentRoutes"
import { Section, Card, Loading, ErrorText, formatDate } from "./ui"

const norm = (value) => String(value || "").toLocaleLowerCase("bg")
// a query finds an item by its name, its author, its pid (what a product page's
// "Open" sends), its order number or a file's name — every word in one of them
const matches = (item, query) => {
  const words = norm(query).split(/\s+/).filter(Boolean)
  if (!words.length) return true
  const hay = [item.name, item.author_display_name, item.pid, item.order?.orderId, ...(item.deliverables || []).map((d) => d.originalFileName || d.name)].map(norm).join(" ")
  return words.every((w) => hay.includes(w))
}

/**
 * Purchased digital downloads: user {deliverableLink} → signed GET
 * /v1/book/deliverable. A search on top narrows the list; its query lives in
 * the url (?q=), so a product page's "Open" lands here on that one product,
 * and a refresh keeps the filter.
 */
export default function DigitalProducts() {
  const t = useT()
  const { orders, pending, error } = useOrders()
  const [busy, setBusy] = useState(null)
  const params = useSearchParams()
  const urlQuery = params.get("q") || ""
  const [query, setQuery] = useState(urlQuery)
  useEffect(() => setQuery(urlQuery), [urlQuery])
  const changeQuery = (value) => {
    setQuery(value)
    setSearchParams({ q: value.trim() || null }, { replace: true })
  }
  const all = orders.flatMap((order) => order.items.filter(isDigitalItem).map((item) => ({ ...item, order })))
  const items = useMemo(() => all.filter((item) => matches(item, query)), [all, query])

  const download = async (item, deliverable) => {
    setBusy(deliverable._id)
    try {
      const link = await DeliverableLink(item.pid, deliverable._id)
      if (!link?.token) return showErrorToast(t(link?.error || "Could not create the download link"))
      const a = document.createElement("a")
      a.href = deliverableUrl(link)
      a.download = deliverable.originalFileName || deliverable.name || ""
      document.body.appendChild(a)
      a.click()
      a.remove()
    } finally {
      setBusy(null)
    }
  }

  return (
    <Section title={t`Digital Products`}>
      {pending && <Loading />}
      <ErrorText>{error?.message}</ErrorText>

      {all.length > 0 && (
        <Input
          variant="aldoo"
          type="search"
          value={query}
          onChange={(e) => changeQuery(e.target.value)}
          placeholder={t`Search your digital products`}
          icon={<MagnifyingGlassIcon className="w-5 h-5 text-text_secondary pointer-events-none" />}
          className="w-full"
          data-testid="digital-products-search"
        />
      )}

      {all.length > 0 && items.length === 0 && 
        <Typography 
          variant="txt1_dim" 
          className="block my-8 text-center"
        >
          {t`No digital products match`}
        </Typography>
      }

      {!pending && !error && all.length === 0 && (
        <div className="py-16 text-center">
          <Typography variant="body1" className="m-0">
            {t`No digital products yet`}
          </Typography>
          <Typography variant="txt1_dim">
            {t`Your digital downloads will appear here after purchase.`}
          </Typography>
        </div>
      )}

      {items.map((item, i) => (
        <Card key={`${item.order._id}-${item.pid}-${i}`} className="flex flex-col md:flex-row gap-4">
          <Button 
            variant="none" 
            href={buildContentUrl(item)} 
            className="shrink-0 md:border md:border-bordered md:rounded-md md:p-1"
          >
            <Img 
              src={item.square || item.thumb} 
              alt={item.name} 
              width={96} 
              height={96} 
              className="w-24 h-24 rounded overflow-hidden" 
            />
          </Button>

          <div className="flex-1 flex flex-col gap-2">
            <Button 
              variant="none" 
              href={buildContentUrl(item)} 
              className="hover:underline"
            >
              <Typography component="span" variant="body1_accent">
              {item.name}
              </Typography>
            </Button>

            {item.author_display_name && 
              <Typography variant="txt2_dim" className="block -mt-2">
                {item.author_display_name}
              </Typography>
            }
            <Typography variant="txt1">
              {t`Order`} #{item.order.orderId} · {formatDate(item.order.date)}
            </Typography>

            {item.deliverables?.length ? (
              <ul className="m-0 p-0 list-none flex flex-col gap-2">
                {item.deliverables.map((d) => (
                  <li key={d._id} className="flex items-center justify-between gap-3 rounded-md bg-neutral px-3 py-2">
                    <span className="flex min-w-0 flex-col">
                      <Typography variant="txt1" className="truncate">
                        {d.originalFileName || d.name || d.deliverableType || "File"}
                      </Typography>

                      {d.deliverableType && 
                        <span className="text-xs uppercase tracking-wide text-text_secondary">
                          {d.deliverableType}
                        </span>
                      }
                    </span>

                    <Button 
                      variant="primary_dark_s" 
                      disabled={busy === d._id} 
                      onClick={() => download(item, d)}
                    >
                      {busy === d._id ? t`Preparing...` : t`Download`}
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <Typography variant="txt1_dim">
                {t`This product is available in the Aldoo app.`}
              </Typography>
            )}
          </div>
        </Card>
      ))}
    </Section>
  )
}
